import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import {
  checkAgentAccess,
  createAgentConversation,
  sendAgentMessage,
  getAgentConversation,
  getMatch,
  getShooters,
  getShooter,
  updateMatch,
  updateShooter,
  registerShooter,
  deleteShooter,
  getScores,
  getStages,
  postAnnouncement,
} from '../../api';

const POLL_INTERVAL = 2000;

// Map tool names to API functions
const TOOL_EXECUTORS = {
  get_match: ({ matchId }) => getMatch(matchId),
  update_match: ({ matchId, data }) => updateMatch(matchId, data),
  list_shooters: ({ matchId }) => getShooters(matchId),
  get_shooter: ({ matchId, shooterId }) => getShooter(matchId, shooterId),
  update_shooter: ({ matchId, shooterId, data }) => updateShooter(matchId, shooterId, data),
  register_shooter: ({ matchId, data }) => registerShooter(matchId, data),
  delete_shooter: ({ matchId, shooterId }) => deleteShooter(matchId, shooterId),
  list_scores: ({ matchId }) => getScores(matchId),
  list_stages: ({ matchId }) => getStages(matchId),
  send_announcement: ({ matchId, data }) => postAnnouncement(matchId, data),
};

function executeJavaScript(code, toolResults) {
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('toolResults', 'Math', 'JSON', `
      let result;
      ${code}
      return result;
    `);
    const res = fn(toolResults, Math, JSON);
    return { success: true, result: res };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

function MessageBubble({ message }) {
  const isUser = message.role === 'user';

  // Don't render tool result messages as visible bubbles
  if (message.toolResults) return null;

  const text = message.text || '';

  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Paper
        elevation={1}
        sx={{
          maxWidth: '80%',
          p: 1.5,
          backgroundColor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'white' : 'text.primary',
          borderRadius: 2,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          '& pre': {
            backgroundColor: isUser ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
            borderRadius: 1,
            p: 1,
            overflow: 'auto',
            fontSize: '0.85rem',
          },
          '& code': {
            fontFamily: 'monospace',
            fontSize: '0.85rem',
          },
        }}
      >
        <MarkdownText text={text} />
      </Paper>
    </Box>
  );
}

function MarkdownText({ text }) {
  if (!text) return null;

  // Simple markdown rendering: code blocks, bold, inline code
  const parts = text.split(/(```[\s\S]*?```)/);

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('```') && part.endsWith('```')) {
          const code = part.slice(3, -3).replace(/^\w*\n/, '');
          return <pre key={i}><code>{code}</code></pre>;
        }
        // Render inline markdown
        return <span key={i}>{renderInlineMarkdown(part)}</span>;
      })}
    </>
  );
}

function renderInlineMarkdown(text) {
  // Split on inline code, bold, etc.
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} style={{ backgroundColor: 'rgba(0,0,0,0.08)', padding: '1px 4px', borderRadius: 3 }}>{part.slice(1, -1)}</code>;
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default function Agent() {
  const { matchId } = useParams();
  const { t } = useTranslation();
  const { match } = useOutletContext();
  const [accessAllowed, setAccessAllowed] = useState(null); // null = loading, true/false
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const toolResultsRef = useRef({});

  // Check agent access on mount
  useEffect(() => {
    checkAgentAccess()
      .then((res) => setAccessAllowed(res.data.allowed))
      .catch(() => setAccessAllowed(false));
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const executeToolCalls = useCallback(async (toolCalls, convId) => {
    const results = [];

    for (const tc of toolCalls) {
      try {
        let result;
        if (tc.name === 'run_javascript') {
          const jsResult = executeJavaScript(tc.input.code, toolResultsRef.current);
          if (jsResult.success) {
            result = jsResult.result;
          } else {
            results.push({ toolUseId: tc.id, result: `Error: ${jsResult.error}`, isError: true });
            continue;
          }
        } else {
          const executor = TOOL_EXECUTORS[tc.name];
          if (!executor) {
            results.push({ toolUseId: tc.id, result: `Unknown tool: ${tc.name}`, isError: true });
            continue;
          }
          const response = await executor(tc.input);
          result = response.data;
        }

        // Store for run_javascript access
        toolResultsRef.current[tc.name] = result;

        // Truncate large results
        const resultStr = JSON.stringify(result);
        const truncated = resultStr.length > 50000
          ? resultStr.slice(0, 50000) + '... (truncated)'
          : resultStr;

        results.push({ toolUseId: tc.id, result: truncated });
      } catch (err) {
        const errMsg = err.response?.data?.detail || err.message || 'Tool execution failed';
        results.push({ toolUseId: tc.id, result: `Error: ${errMsg}`, isError: true });
      }
    }

    // Send tool results back
    await sendAgentMessage(convId, { toolResults: results });
  }, []);

  const poll = useCallback(async (convId) => {
    try {
      const res = await getAgentConversation(convId);
      const conv = res.data;
      setMessages(conv.messages.filter((m) => !m.toolResults));
      setStatus(conv.status);

      if (conv.status === 'tool_calls') {
        // Find the last assistant message with tool calls
        const lastAssistant = [...conv.messages].reverse().find((m) => m.role === 'assistant' && m.toolCalls);
        if (lastAssistant) {
          await executeToolCalls(lastAssistant.toolCalls, convId);
          // Continue polling after sending tool results
          pollRef.current = setTimeout(() => poll(convId), POLL_INTERVAL);
        }
      } else if (conv.status === 'pending' || conv.status === 'processing') {
        pollRef.current = setTimeout(() => poll(convId), POLL_INTERVAL);
      }
    } catch (err) {
      console.error('Poll error:', err);
      setError(t('agent.error', 'Something went wrong. Please try again.'));
      setStatus('error');
    }
  }, [executeToolCalls, t]);

  const handleSend = async () => {
    if (!input.trim() || status === 'pending' || status === 'processing') return;

    const text = input.trim();
    setInput('');
    setError(null);

    try {
      let convId = conversationId;
      if (!convId) {
        const res = await createAgentConversation(matchId);
        convId = res.data.id;
        setConversationId(convId);
      }

      // Optimistically add user message
      setMessages((prev) => [...prev, { role: 'user', text, timestamp: new Date().toISOString() }]);
      setStatus('pending');

      await sendAgentMessage(convId, { text });

      // Start polling
      pollRef.current = setTimeout(() => poll(convId), POLL_INTERVAL);
    } catch (err) {
      setError(t('agent.error', 'Something went wrong. Please try again.'));
      setStatus('idle');
    }
  };

  const handleNewConversation = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    setConversationId(null);
    setMessages([]);
    setStatus('idle');
    setError(null);
    toolResultsRef.current = {};
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isLoading = status === 'pending' || status === 'processing' || status === 'tool_calls';

  if (accessAllowed === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!accessAllowed) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('agent.noAccess', 'You do not have access to the AI assistant. Contact an administrator to request access.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', maxHeight: 800 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">{t('agent.title', 'AI Assistant')}</Typography>
        {conversationId && (
          <Button
            startIcon={<AddIcon />}
            onClick={handleNewConversation}
            size="small"
          >
            {t('agent.newConversation', 'New Conversation')}
          </Button>
        )}
      </Box>

      {/* Messages */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          mb: 2,
          backgroundColor: 'background.default',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {messages.length === 0 && !isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
            <Typography variant="body1" color="text.secondary">
              {t('agent.empty', `Ask me anything about "${match?.title || 'your match'}". I can help manage shooters, check scores, send announcements, and more.`)}
            </Typography>
          </Box>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="body2">{t('agent.thinking', 'Thinking...')}</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div ref={messagesEndRef} />
      </Paper>

      {/* Input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('agent.placeholder', 'Type a message...')}
          disabled={isLoading}
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': { backgroundColor: 'primary.dark' },
            '&.Mui-disabled': { backgroundColor: 'grey.300', color: 'grey.500' },
            borderRadius: 2,
            width: 40,
            height: 40,
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
